import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getHyperPayCheckout,
  getHyperPayCheckoutError,
  type HyperpayConfigShape,
} from "utils/hyperpayCheckout.util";
import { canUseApplePayInBrowser } from "utils/applePay.util";
import { HyperPayPaymentForm } from "./HyperPayPaymentForm";

const HYPERPAY_UNIFIED_BRANDS = "APPLEPAY MADA VISA MASTER";
const hyperPayUnifiedBrands = () =>
  canUseApplePayInBrowser() ? HYPERPAY_UNIFIED_BRANDS : "MADA VISA MASTER";
const HYPERPAY_SPLIT_CARD_BRANDS = "MADA VISA MASTER";

type PartnerHyperPayCheckoutProps = {
  hyperpay?: HyperpayConfigShape | null;
};

export default function PartnerHyperPayCheckout({
  hyperpay,
}: PartnerHyperPayCheckoutProps) {
  const { t } = useTranslation("translation");
  const hyperPayCheckout = getHyperPayCheckout(hyperpay);
  const hyperPayCheckoutError = getHyperPayCheckoutError(hyperpay);
  const isSplitHyperPay = hyperPayCheckout?.mode === "split";
  const isUnifiedHyperPay = hyperPayCheckout?.mode === "unified";
  const hyperpayResolved = hyperPayCheckout?.config ?? null;
  const canShowHyperPayAppleTab = canUseApplePayInBrowser();
  const [hyperPayTab, setHyperPayTab] = useState<"card" | "applepay">("card");

  useEffect(() => {
    if (isSplitHyperPay && !canShowHyperPayAppleTab && hyperPayTab === "applepay") {
      setHyperPayTab("card");
    }
  }, [isSplitHyperPay, canShowHyperPayAppleTab, hyperPayTab]);

  if (!hyperPayCheckout || !hyperpayResolved) {
    return (
      <p className="yp-payment__hyperpay-error">
        {hyperPayCheckoutError ||
          String(t("paymentFormLoadError", "Payment form could not be loaded."))}
      </p>
    );
  }

  return (
    <div className="yp-payment__hyperpay">
      {isSplitHyperPay && canShowHyperPayAppleTab ? (
        <div className="yp-payment__hyperpay-tabs">
          <button
            type="button"
            className={`yp-payment__hyperpay-tab${
              hyperPayTab === "card" ? " is-active" : ""
            }`}
            onClick={() => setHyperPayTab("card")}
          >
            {String(t("payWithCard", "Pay with card"))}
          </button>
          <button
            type="button"
            className={`yp-payment__hyperpay-tab${
              hyperPayTab === "applepay" ? " is-active" : ""
            }`}
            onClick={() => setHyperPayTab("applepay")}
          >
            {String(t("payWithApplePay", "Apple Pay"))}
          </button>
        </div>
      ) : null}

      <div className="App-payment">
        {isUnifiedHyperPay ? (
          <HyperPayPaymentForm
            key={`hyperpay-unified-${hyperpayResolved.checkoutId}`}
            config={{
              checkoutId: hyperpayResolved.checkoutId,
              widgetBaseUrl: hyperpayResolved.widgetBaseUrl,
              return_url: hyperpayResolved.return_url,
              checkoutIntegrity: hyperpayResolved.checkoutIntegrity,
              wpwlOptions: hyperpayResolved.wpwlOptions,
            }}
            scriptId="partner-hyperpay-unified-widget"
            dataBrands={hyperPayUnifiedBrands()}
          />
        ) : isSplitHyperPay && hyperPayTab === "card" ? (
          <HyperPayPaymentForm
            key={`hyperpay-card-${hyperpayResolved.checkoutIdCard}`}
            config={{
              checkoutId: hyperpayResolved.checkoutIdCard,
              widgetBaseUrl: hyperpayResolved.widgetBaseUrl,
              return_url: hyperpayResolved.return_url,
              checkoutIntegrity: hyperpayResolved.checkoutIntegrityCard,
              wpwlOptions: hyperpayResolved.wpwlOptions,
            }}
            scriptId="partner-hyperpay-card-widget"
            dataBrands={HYPERPAY_SPLIT_CARD_BRANDS}
          />
        ) : isSplitHyperPay && hyperPayTab === "applepay" ? (
          <HyperPayPaymentForm
            key={`hyperpay-apple-${hyperpayResolved.checkoutIdApplePay}`}
            config={{
              checkoutId: hyperpayResolved.checkoutIdApplePay,
              widgetBaseUrl: hyperpayResolved.widgetBaseUrl,
              return_url: hyperpayResolved.return_url,
              checkoutIntegrity: hyperpayResolved.checkoutIntegrityApplePay,
              wpwlOptions: hyperpayResolved.wpwlOptions,
            }}
            scriptId="partner-hyperpay-apple-widget"
            variant="applepay"
          />
        ) : isSplitHyperPay ? (
          <HyperPayPaymentForm
            key={`hyperpay-card-fb-${hyperpayResolved.checkoutIdCard}`}
            config={{
              checkoutId: hyperpayResolved.checkoutIdCard,
              widgetBaseUrl: hyperpayResolved.widgetBaseUrl,
              return_url: hyperpayResolved.return_url,
              checkoutIntegrity: hyperpayResolved.checkoutIntegrityCard,
              wpwlOptions: hyperpayResolved.wpwlOptions,
            }}
            scriptId="partner-hyperpay-card-fb"
            dataBrands={HYPERPAY_SPLIT_CARD_BRANDS}
          />
        ) : null}
      </div>
    </div>
  );
}
